/// <amd-module name="Wiki/Overview/WikiPageMenuItemsProvider" />
import * as React from "react";

import { startsWith } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { onClickFPS } from "VSS/Platform/FPS";
import { StateMergeOptions } from "VSS/Platform/History";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem, IMenuProps } from "VSSUI/Menu";

import { GitVersionDescriptor } from "Wiki/Clients/VC/Contracts/Git";
import { WikiPage, WikiType, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { ActionCreatorsHub } from "Wiki/Overview/Actions/ActionCreatorsHub";
import * as Dialogs from "Wiki/Overview/OverviewDialogHandler";
import * as Resources from "Wiki/Overview/Resources";
import { State } from "Wiki/Overview/Stores/StoresHub";
import { copyPagePathToClipboard } from "Wiki/Overview/Utils/WikiHelper";
import { publishWikiPageCommandClicked } from "Wiki/Overview/Utils/WikiTelemetryHelper";
import { ActionIds, RepoServiceContributionId, UrlConstants } from "Wiki/ViewCommon/Constants";
import { WikiPageInfo, WikiPermission } from "Wiki/ViewCommon/Contracts";
import { TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { getFileName, getParentPath, isInclude } from "Wiki/ViewCommon/Utils/PathHelper";
import {
    getPagePathBasedUrlAndState,
    getVersionFromBranchName,
    getWikiTypeString,
    getWikiUrl,
    isCodeEditEnabled,
    isFirefox,
    isIncludeEditingAllowed,
    isSimpleUrlEnabled
} from "Wiki/ViewCommon/Utils/WikiHelper";

const c_editPageId = "edit-page";
const c_printPageId = "print-page";

interface CommandCreator {
    (options: CommandsOptions, index?: number): IMenuItem;
}

interface CommandsOptions {
    page: WikiPage;
    actionCreator: ActionCreatorsHub;
    pageContext: IVssPageContext;
    wiki: WikiV2;
    version: GitVersionDescriptor;
    logTelemetry: (feature: string) => void;
    state: () => State;
    wikiPermission: WikiPermission;
    isHandleBrokenLinksEnabled: boolean;
}

const creators: { [key: string]: CommandCreator } = {
    viewRevisions: (options: CommandsOptions): IMenuItem => {
        // Keeping block instead of an expression to avoid breaking page in Edge 14/15
        // https://github.com/microsoft/ChakraCore/issues/2606
        const { page, pageContext, logTelemetry, wiki, version } = options;
        return {
            id: "view-revision",
            text: Resources.PageRevisionsMenuItemText,
            onActivate: (_: IMenuItem, event) => {
                logTelemetry(TelemetryConstants.ViewPageHistoryButtonClicked);
                _navigateToPageRevisions(pageContext, wiki, version, page.path, event);
            },
            iconProps: {
                iconName: "FullHistory"
            },
            disabled: page.isNonConformant
        };
    },

    editPage: (options: CommandsOptions): IMenuItem => {
        const { page, actionCreator, logTelemetry, wikiPermission } = options;
        return {
            id: c_editPageId,
            text: Resources.EditPageMenuItemText,
            onActivate: () => {
                logTelemetry(TelemetryConstants.EditButtonClicked);
                actionCreator.startEditing(page.path);
            },
            iconProps: {
                iconName: "Edit"
            },
            disabled: !wikiPermission.hasWikiContributePermission || page.isNonConformant
        };
    },

    editInRepos: (options: CommandsOptions): IMenuItem => {
        const { page, pageContext, actionCreator, logTelemetry, wikiPermission } = options;
        return {
            id: c_editPageId, // id is same as edit page
            text: Resources.EditInReposMenuItemText,
            onActivate: () => {
                logTelemetry(TelemetryConstants.EditButtonClicked);
                actionCreator.redirectToCode(page.path);
            },
            iconProps: {
                iconName: "Edit"
            },
            disabled: !wikiPermission.hasWikiContributePermission || !isFeatureEnabled(pageContext, RepoServiceContributionId) || page.isNonConformant
        };
    },

    copyPagePath: (options: CommandsOptions): IMenuItem => {
        const { page, pageContext, logTelemetry } = options;
        return {
            id: "copy-page-path",
            text: Resources.CopyPagePathMenuItemText,
            onActivate: () => {
                logTelemetry(TelemetryConstants.CopyPagePathClicked);
                copyPagePathToClipboard(pageContext, page.path);
            },
            iconProps: {
                iconName: "Copy"
            },
            disabled: page.isNonConformant
        };
    },

    printPage: (options: CommandsOptions): IMenuItem => {
        const { page, pageContext, logTelemetry, state, actionCreator } = options;
        return {
            id: "print-page",
            text: Resources.PrintPageMenuItemText,
            onActivate: () => {
                logTelemetry(TelemetryConstants.PagePrinted);
                actionCreator.printCurrentPage();
            },
            iconProps: {
                iconName: "Print"
            },
            disabled:
                state().overviewState.isEditing ||
                !_isMenuForCurrentPage(state().overviewState.pageInfo, page) ||
                page.isNonConformant ||
                _isIncludeWithEditingDisabled(pageContext, page.path)
        };
    },

    deletePage: (options: CommandsOptions): IMenuItem => {
        const { page, pageContext, actionCreator, wiki, version, logTelemetry, wikiPermission, state } = options;
        return {
            id: "delete-page",
            text: Resources.DeletePageText,
            onActivate: () => {
                logTelemetry(TelemetryConstants.DeletePageButtonClicked);

                Dialogs.showDeletePageDialog(pageContext, {
                    wiki: wiki,
                    version: version,
                    page: page,
                    onPageDelete: actionCreator.onPageDelete
                });
            },
            iconProps: {
                iconName: "Delete"
            },
            disabled:
                !wikiPermission.hasWikiContributePermission ||
                state().overviewState.isEditing ||
                page.isNonConformant ||
                _isIncludeWithEditingDisabled(pageContext, page.path)
        };
    },

    openInNewTab: (options: CommandsOptions): IMenuItem => {
        const { actionCreator, logTelemetry, page } = options;
        return {
            id: "open-in-new-tab",
            text: Resources.OpenInNewTabText,
            onActivate: () => {
                logTelemetry(TelemetryConstants.OpenInNewTabClicked);
                actionCreator.openInNewTab(page.path);
            },
            iconProps: {
                iconName: "OpenInNewTab"
            },
            disabled: page.isNonConformant
        };
    },

    movePage: (options: CommandsOptions): IMenuItem => {
        const { page, wiki, version, pageContext, actionCreator, logTelemetry, wikiPermission, state, isHandleBrokenLinksEnabled } = options;
        return {
            id: "move-page",
            text: Resources.MovePageMenuItem,
            onActivate: () => {
                const title = getFileName(page.path);
                const pageInfo = state().overviewState.pageInfo;
                logTelemetry(TelemetryConstants.MovePagePickerPanelPrompted);
                Dialogs.showMovePagePanel(pageContext, {
                    wiki,
                    version,
                    title,
                    sourcePage: page,
                    isHandleBrokenLinksEnabled,
                    makePageAsSubPage: actionCreator.treeAC.makePageAsSubPage,
                    isReorderPagesAllowed: _isReorderPagesAllowed,
                    homePagePath: state().treeState.homePagePath,
                    selectedPagePath: pageInfo && pageInfo.path,
                    getPagesForFiltering: actionCreator.treeAC.getPagesForFiltering
                });
            },
            iconProps: {
                iconName: "PageArrowRight"
            },
            disabled:
                !wikiPermission.hasWikiContributePermission ||
                state().overviewState.isEditing ||
                page.isNonConformant ||
                _isIncludeWithEditingDisabled(pageContext, page.path)
        };
    },

    linkWorkItems: (options: CommandsOptions): IMenuItem => {
        const { page, pageContext, logTelemetry, wiki, actionCreator, wikiPermission, state } = options;
        return {
            id: "link-work-items",
            text: Resources.LinkWitMenuItemText,
            onActivate: () => {
                logTelemetry(TelemetryConstants.LinkWitButtonClicked);
                const title = getFileName(page.path);

                Dialogs.showLinkWorkItemPanel(pageContext, {
                    title: title,
                    wikiId: wiki.id,
                    wikiPagePath: page.path,
                    wikiType: getWikiTypeString(wiki.type),
                    onUpdateButtonClick: actionCreator.updateLinkedWit,
                    hasWikiContributePermission: wikiPermission.hasWikiContributePermission
                });
            },
            iconProps: {
                iconName: "Link"
            },
            disabled: state().overviewState.isEditing || page.isNonConformant || isInclude(page.path)
        };
    },

    addSubPage: (options: CommandsOptions): IMenuItem => {
        const { page, pageContext, logTelemetry, actionCreator, wikiPermission } = options;
        return {
            id: "addSubPage",
            text: Resources.AddSubPage,
            onActivate: () => {
                logTelemetry(TelemetryConstants.AddSubPage);
                actionCreator.addSubPage(page.path);
            },
            iconProps: {
                iconName: "Add"
            },
            disabled: !wikiPermission.hasWikiContributePermission || page.isNonConformant || _isIncludeWithEditingDisabled(pageContext, page.path)
        };
    }
};

const treeItemCommands: CommandCreator[] = [
    creators.addSubPage,
    creators.copyPagePath,
    creators.movePage,
    creators.editPage,
    creators.deletePage,
    creators.openInNewTab
];

const codeWikiTreeItemCommands: CommandCreator[] = [creators.editInRepos, creators.openInNewTab];

const headerMenuItems: CommandCreator[] = [creators.printPage, creators.linkWorkItems, creators.viewRevisions, creators.deletePage];

const headerMenuItemsForCodeWikiEditing: CommandCreator[] = [creators.printPage, creators.linkWorkItems, creators.viewRevisions, creators.deletePage];

const codeWikiHeaderMenuItems = [creators.printPage, creators.linkWorkItems, creators.viewRevisions];

export class WikiPageMenuItemsProvider {
    constructor(
        private _pageContext: IVssPageContext,
        private _wiki: WikiV2,
        private _version: GitVersionDescriptor,
        private _actionCreator: ActionCreatorsHub,
        private _getState: () => State,
        private _wikiPermission: WikiPermission,
        private _isHandleBrokenLinksEnabled: boolean
    ) {}

    public getTreeMenuItems = (page: WikiPage): IMenuProps => {
        const menuId = "tree-node-menu";

        const commandOptions: CommandsOptions = {
            page,
            actionCreator: this._actionCreator,
            pageContext: this._pageContext,
            wiki: this._wiki,
            version: this._version,
            logTelemetry: (feature: string) =>
                this._logTelemetry(feature, menuId, page.id, _isMenuForCurrentPage(this._getState().overviewState.pageInfo, page)),
            state: this._getState,
            wikiPermission: this._wikiPermission,
            isHandleBrokenLinksEnabled: this._isHandleBrokenLinksEnabled
        };

        if (!isCodeEditEnabled(this._pageContext) && this._wiki.type === WikiType.CodeWiki) {
            return _createMenuProp(menuId, codeWikiTreeItemCommands, commandOptions);
        }

        return _createMenuProp(menuId, treeItemCommands, commandOptions);
    };

    public getHeaderMenuItems = (page: WikiPage): IHeaderCommandBarItem[] => {
        const commandOptions: CommandsOptions = {
            page,
            actionCreator: this._actionCreator,
            pageContext: this._pageContext,
            wiki: this._wiki,
            version: this._version,
            logTelemetry: (feature: string) => this._logTelemetry(feature, "header-menu", page.id, true),
            state: this._getState,
            wikiPermission: this._wikiPermission,
            isHandleBrokenLinksEnabled: this._isHandleBrokenLinksEnabled
        };

        let headerItems = headerMenuItems;

        if (this._wiki.type === WikiType.CodeWiki) {
            if (!isCodeEditEnabled(this._pageContext)) {
                headerItems = codeWikiHeaderMenuItems;
            } else {
                headerItems = headerMenuItemsForCodeWikiEditing;
            }
        }

        const menuItems: IHeaderCommandBarItem[] = _create(headerItems, commandOptions).map(value => {
            return { ...value, important: false };
        });

        return menuItems;
    };

    private _logTelemetry(feature: string, source: string, pageId?: number, currentPage?: boolean): void {
        const mode = this._getState().overviewState.isEditing ? TelemetryConstants.EditMode : TelemetryConstants.ViewMode;
        publishWikiPageCommandClicked(this._pageContext, feature, source, this._wiki.type, pageId, mode, currentPage);
    }
}

function _createMenuProp(creatorId: string, creators: CommandCreator[], options: CommandsOptions): IMenuProps {
    return {
        id: creatorId,
        items: _create(creators, options)
    };
}

function _create(creators: CommandCreator[], options: CommandsOptions): IMenuItem[] {
    return creators
        .map((creator: CommandCreator, index: number) => creator(options, index))
        .filter((item: IMenuItem) => {
            if (item.id === c_printPageId) {
                return !isFirefox();
            }

            return true;
        });
}

// Exported for UT
export function _navigateToPageRevisions(
    pageContext: IVssPageContext,
    wiki: WikiV2,
    version: GitVersionDescriptor,
    pagePath: string,
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
): void {
    let newUrl: string;
    if (!isSimpleUrlEnabled(pageContext)) {
        newUrl = getWikiUrl(pageContext, { pagePath, [UrlConstants.ActionParam]: ActionIds.History }, StateMergeOptions.routeValues);
    } else {
        // since we support simple URL only for view, force it to be path based template on other places like revision, edit..etc
        // else by default it would pick simple URL format which doesn't have page path and so doesn't work
        newUrl = getPagePathBasedUrlAndState(
            pageContext,
            {
                pagePath,
                [UrlConstants.ActionParam]: ActionIds.History
            },
            { wikiIdentifier: wiki.name, wikiVersion: getVersionFromBranchName(version.version) }
        ).url;
    }

    if (event && event.ctrlKey) {
        const openedWindow = window.open(newUrl, "_blank");
        if (openedWindow) {
            openedWindow.opener = null;
        }
    } else {
        onClickFPS(pageContext, newUrl, true, event);
    }
}

function _isReorderPagesAllowed(sourcePage: WikiPage, targetPage: WikiPage): boolean {
    if (!sourcePage || sourcePage.isNonConformant || !targetPage || targetPage.isNonConformant || sourcePage.path === targetPage.path) {
        return false;
    }

    // TODO: update this once editor is done
    // if (this.isTempPagePath(targetPage.path) || this.isEditInProgress()) {
    //     // Temp page and all pages in edit mode are not valid drop targets
    //     return false;
    // }
    const isTargetDescendantOfSource = startsWith(targetPage.path, sourcePage.path + "/");
    const isTargetImmediateParentOfSource = getParentPath(sourcePage.path) === targetPage.path;

    return !isTargetImmediateParentOfSource && !isTargetDescendantOfSource;
}

function _isMenuForCurrentPage(currentPageInfo: WikiPageInfo | undefined, menuPage: WikiPage): boolean {
    if (currentPageInfo) {
        return currentPageInfo.path === menuPage.path;
    }

    return false;
}

/**
 * Returns true if the
 *    1. Page is an include
 *    2. Includes are disabled in wiki hub.
 */
function _isIncludeWithEditingDisabled(pageContext: IVssPageContext, path: string): boolean {
    return !isIncludeEditingAllowed(pageContext) && isInclude(path);
}
